'use strict';

Object.defineProperty(exports, '__esModule', { value: true });

var baseBus = require('./base-bus.js');
var payloadMismatchError = require('./payload-mismatch-error.js');

var __async = (__this, __arguments, generator) => {
  return new Promise((resolve, reject) => {
    var fulfilled = (value) => {
      try {
        step(generator.next(value));
      } catch (e) {
        reject(e);
      }
    };
    var rejected = (value) => {
      try {
        step(generator.throw(value));
      } catch (e) {
        reject(e);
      }
    };
    var step = (x) => x.done ? resolve(x.value) : Promise.resolve(x.value).then(fulfilled, rejected);
    step((generator = generator.apply(__this, __arguments)).next());
  });
};
const logCss = "background-color: dodgerblue; color: white";
const eventReplay = "EVENT_REPLAY";
class EventBus extends baseBus.BaseBus {
  constructor() {
    super(...arguments);
    this._lastId = 0;
  }
  subscribe(param1, param2, options) {
    if (typeof param2 !== "function") {
      throw new Error("Callback function must be supplied as the second argument.");
    }
    if (typeof param1 === "string") {
      const channel = param1;
      const callback = param2;
      return this.subscribeSingleChannel(channel, callback, options);
    }
    const channels = param1;
    const multiCallback = param2;
    return this.subscribeMultipleChannels(channels, multiCallback, options);
  }
  publish(channel, payload) {
    const schema = this.getSchema(channel);
    if (this.isPayloadInvalid(channel, payload)) {
      throw new payloadMismatchError.PayloadMismatchError(channel, schema, payload);
    }
    this.setLatest(channel, payload);
    if (!this.getHandlers(channel).length) {
      this.logMissingHandler(channel);
      return;
    }
    this.logPublishedMessage(channel, payload);
    return this.resolveHandlers(channel, payload);
  }
  getNextId() {
    return this._lastId++;
  }
  getHandlers(channel) {
    var _a, _b, _c;
    return Array.from((_c = (_b = (_a = this._subscriptions.get(channel)) == null ? void 0 : _a.handlers) == null ? void 0 : _b.entries()) != null ? _c : []);
  }
  setHandler(channel, callback, handlerId, options) {
    if (!this._subscriptions.has(channel)) {
      this.initChannel(channel);
    }
    const subscription = this._subscriptions.get(channel);
    subscription.handlers.set(handlerId, callback);
    if (options) {
      subscription.handlerOptions.set(handlerId, options);
    }
  }
  resolveHandlers(channel, payload) {
    return new Promise((resolve, reject) => __async(this, null, function* () {
      try {
        yield Promise.all(this.getHandlers(channel).map(([handlerId, handler]) => {
          const options = this._subscriptions.get(channel).handlerOptions.get(handlerId);
          if ((options == null ? void 0 : options.distinct) && this.isDuplicateValueEmitted(channel)) {
            return;
          }
          return handler({
            channel,
            payload
          });
        }));
        resolve();
      } catch (e) {
        reject(e);
      }
    }));
  }
  canReplay(channel) {
    return typeof this.getLatest(channel) !== "undefined";
  }
  replayHandler(channel, handler) {
    const payload = this.getLatest(channel);
    return new Promise((resolve) => {
      handler({
        channel,
        payload
      });
      resolve();
    });
  }
  getSubscription(channel, handlerId) {
    return {
      unsubscribe: () => {
        this._subscriptions.get(channel).handlers.delete(handlerId);
        this._subscriptions.get(channel).handlerOptions.delete(handlerId);
      }
    };
  }
  subscribeHandler(channel, callback, options) {
    this.logRegisteredHandler(channel);
    const handlerId = this.getNextId();
    this.setHandler(channel, callback, handlerId, options);
    return this.getSubscription(channel, handlerId);
  }
  subscribeSingleChannel(channel, callback, options) {
    if ((options == null ? void 0 : options.replay) && this.canReplay(channel)) {
      this.logHandlerReplay(channel);
      Promise.resolve().then(() => {
        this.replayHandler(channel, callback);
      });
    }
    return this.subscribeHandler(channel, callback, options);
  }
  subscribeMultipleChannels(channels, callback, options) {
    const multiChannelCallback = (event) => {
      if (channels.some((channel) => !this.canReplay(channel))) {
        return;
      }
      callback({
        channel: event.channel,
        payload: this.getLatestMultiChannel(channels)
      });
    };
    if (options == null ? void 0 : options.replay) {
      this.logMultiHandlerReplay(channels);
      Promise.resolve().then(() => {
        multiChannelCallback({
          channel: eventReplay,
          payload: void 0
        });
      });
    }
    const subscriptions = channels.map((channel) => {
      return this.subscribeHandler(channel, multiChannelCallback, options);
    });
    return {
      unsubscribe() {
        subscriptions.forEach((subscription) => {
          subscription.unsubscribe();
        });
      }
    };
  }
  initChannel(channel) {
    super.initChannel(channel);
    this._subscriptions.get(channel).handlers = new Map();
    this._subscriptions.get(channel).handlerOptions = new Map();
  }
  logRegisteredHandler(channel) {
    this.log("%cEvent bus: registered handler for channel", logCss, channel);
  }
  logMissingHandler(channel) {
    this.log("%cEvent bus: handler is missing for channel", logCss, channel);
  }
  logPublishedMessage(channel, payload) {
    this.log("%cEvent bus: publishing message for channel", logCss, channel);
    this.log("%cEvent bus: message payload", logCss, payload);
  }
  logHandlerReplay(channel) {
    const payload = this.getLatest(channel);
    this.log("%cEvent bus: replay handler for channel", logCss, channel);
    this.log("%cEvent bus: replayed payload", logCss, payload);
  }
  logMultiHandlerReplay(channels) {
    const payload = this.getLatestMultiChannel(channels);
    this.log("%cEvent bus: replay handler for channel", logCss, channels);
    this.log("%cEvent bus: replayed payload", logCss, payload);
  }
  getLatestMultiChannel(channels) {
    return channels.map((channel) => this.getLatest(channel));
  }
}

exports.EventBus = EventBus;
exports.eventReplay = eventReplay;
